import { Lock, TriangleAlert } from 'lucide-react'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'
import { describeProblem, fieldErrors, fieldErrorKey } from '@/lib/problems'
import { rateMissingMessage } from '@/lib/refusals'

/* Errors state what happened, then what to do, and NEVER apologise. Blocking
 * messages name the block and the reason together, so the rule is learned once
 * rather than rediscovered. */

export interface ErrorStateProps {
  error: unknown
  onRetry?: () => void
  /** What the button actually DOES, when it is not "try again".
   *
   *  Some callers have no request to re-issue — a panel showing four mutations'
   *  refusals can only clear the one the reader is looking at — and labelling
   *  that "Try again" promises a second attempt that never happens. The
   *  handler is still `onRetry` because the position and the shape are the
   *  same; only the word changes. */
  retryLabel?: string
  className?: string
}

/* The message comes from the problem's TYPE SUFFIX, never from its title or
 * detail (AD-22, AD-27).
 *
 * This function used to pass the server's own `problem.title` and
 * `problem.detail` through for anything that was not a 403, 404 or 5xx. Those
 * strings are written server-side in English for a developer reading a log, so
 * a Russian user hitting a validation failure or a conflict read an English
 * sentence — in the moment they were least able to cope with a second language.
 *
 * The suffix is the contract the server commits to; the words are ours. */
function describe(error: unknown, t: TFunction) {
  const { titleKey, detailKey, retryable } = describeProblem(error)

  return {
    title: t(titleKey),
    /* One suffix cannot be worded from the suffix alone: `rate-missing`
       carries the currency and the day it had no rate for, and only those say
       whether to wait for the hourly fill or to ask an administrator. Asked
       here rather than at each caller, because every surface that renders a
       write's refusal as a block — the case list's create first — would
       otherwise print the generic sentence over members it was handed. */
    detail: rateMissingMessage(error, t) ?? t(detailKey),
    retryable,
    /* Field-level failures, so a record-level error can still say WHICH fields
       need attention rather than only that some do. */
    fields: fieldErrors(error),
  }
}

export function ErrorState({ error, onRetry, retryLabel, className }: ErrorStateProps) {
  const { t } = useTranslation()
  const { title, detail, retryable, fields } = describe(error, t)
  const fieldNames = Object.keys(fields)
  const forbidden = error instanceof ApiError && error.isForbidden
  const Icon = forbidden ? Lock : TriangleAlert

  return (
    <div
      role="alert"
      className={cn(
        'border-line bg-surface flex flex-col items-center justify-center gap-2',
        'rounded-[var(--radius-DEFAULT)] border px-6 py-12 text-center',
        className,
      )}
    >
      <Icon
        size={20}
        aria-hidden
        className={forbidden ? 'text-text-muted' : 'text-attention'}
      />
      <p className="text-text-heading text-[13.5px] font-normal">{title}</p>
      <p className="text-text-muted max-w-[46ch] text-[12.5px] leading-snug font-light">
        {detail}
      </p>
      {/* Which fields, not merely that some. A record-level error that says
          "some values need attention" and stops is a message the reader has to
          go hunting behind (UX-DR-18). */}
      {fieldNames.length > 0 ? (
        <ul className="text-text-muted mt-1 flex flex-col gap-0.5 text-[12.5px] font-light">
          {fieldNames.map((field) => (
            <li key={field}>
              <span className="text-text-body">{field}</span>
              {': '}
              {(fields[field] ?? []).map((code) => t(fieldErrorKey(code))).join(', ')}
            </li>
          ))}
        </ul>
      ) : null}

      {/* `retryable` gates the RETRY only. It is the problem type's own answer
          to "would asking again help", and a 422 or a 409 answers no — which is
          why a refused save shows no button here at all. A caller that has
          named the action is not making that claim: dismissing a refusal is
          worth offering precisely on the problems that will never succeed. */}
      {onRetry && (retryable || retryLabel !== undefined) ? (
        <Button variant="secondary" size="sm" className="mt-1" onClick={onRetry}>
          {retryLabel ?? t('common.actions.retry')}
        </Button>
      ) : null}
    </div>
  )
}
